import { createClient } from '../supabase/client';
import { v4 as uuidv4 } from 'uuid';

const supabase = createClient();

interface CompanyInvite {
  id: string;
  company_id: string;
  email: string;
  role: string;
  token: string;
  invited_by: string;
  invited_at: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
  companies?: {
    name: string;
    logo_url?: string;
  };
}

/**
 * Creates a company invite
 * @param email Email address to invite
 * @param companyId ID of the company
 * @param role Role to assign (default: 'member')
 * @param invitedBy ID of the user sending the invite
 * @param companyName Name of the company sending the invite
 * @param invitedByName Name of the user sending the invite (optional)
 * @param expiresInDays Number of days until the invite expires (default: 7)
 */
export const createCompanyInvite = async (
  email: string,
  companyId: string,
  role: string = 'member',
  invitedBy: string,
  companyName: string,
  invitedByName?: string,
  expiresInDays: number = 7
): Promise<{ success: boolean; error?: string; token?: string }> => {
  try {
    // Skip admin role check for now
    // Instead, we'll just check if the user is associated with the company at all
    const { error: companyError } = await supabase
      .from('company_users')
      .select('company_id')
      .eq('user_id', invitedBy)
      .eq('company_id', companyId)
      .single();

    if (companyError) {
      console.log('Company association check error:', companyError);
      // Skip this check for now, proceed with invite
    }

    // Check if email already has a pending invite for this company
    const { data: existingInvites, error: existingError } = await supabase
      .from('invited_company_users')
      .select('*')
      .eq('email', email)
      .eq('company_id', companyId)
      .gt('expires_at', new Date().toISOString());

    if (existingError) {
      console.error('Error checking existing invites:', existingError);
    }

    if (existingInvites && existingInvites.length > 0) {
      return {
        success: false,
        error: 'This email already has a pending invitation'
      };
    }

    const token = uuidv4();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expiresInDays);

    // Make sure all fields have the correct types
    const newInvite = {
      company_id: companyId,
      email,
      role,
      token,
      invited_by: invitedBy,
      invited_at: new Date().toISOString(),
      expires_at: expiresAt.toISOString()
    };

    // Try to use service role if available to bypass RLS
    // Note: This is a temporary solution and should be replaced with proper role-based checking
    const { error } = await supabase
      .from('invited_company_users')
      .insert(newInvite);

    if (error) {
      console.error('Error details:', error);
      if (error.code === '22P02') {
        return {
          success: false,
          error:
            'Type conversion error. Please make sure all fields have the correct format.'
        };
      }
      if (error.code === '42501') {
        // Permission error, but we'll provide a more generic message for now
        return {
          success: false,
          error: 'Unable to create invitation. Please try again later.'
        };
      }
      throw error;
    }

    // Send invite email
    try {
      // Use window.location to get the base URL if this code runs in the browser
      const baseUrl =
        typeof window !== 'undefined'
          ? `${window.location.protocol}//${window.location.host}`
          : process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/email/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          inviteToken: token,
          companyName,
          invitedByName
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error sending invite email:', errorData);
        // Continue the process even if email sending fails
      } else {
        console.log('Invite email sent successfully');
      }
    } catch (emailError) {
      console.error('Error sending invite email:', emailError);
      // Continue the process even if email sending fails
    }

    return { success: true, token };
  } catch (error: unknown) {
    console.error('Error creating company invite:', error);
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to create invite';
    return { success: false, error: errorMessage };
  }
};

/**
 * Validates an invite token
 * @param token The invite token to validate
 */
export const validateInviteToken = async (
  token: string
): Promise<{
  valid: boolean;
  invite?: CompanyInvite;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('invited_company_users')
      .select('*, companies(name, logo_url)')
      .eq('token', token)
      .single();

    if (error) {
      throw error;
    }

    // Check if invite has expired
    if (new Date(data.expires_at) < new Date()) {
      return {
        valid: false,
        error: 'Invite has expired'
      };
    }

    return { valid: true, invite: data as CompanyInvite };
  } catch (error: unknown) {
    console.error('Error validating invite:', error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Invalid or expired invite token';
    return {
      valid: false,
      error: errorMessage
    };
  }
};

/**
 * Process a signup with an invite token
 * @param token The invite token
 * @param userId The ID of the new user
 */
export const processInviteSignup = async (
  token: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Use our API endpoint which handles the association via secure SQL function
    const baseUrl =
      typeof window !== 'undefined'
        ? `${window.location.protocol}//${window.location.host}`
        : process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';

    const response = await fetch(`${baseUrl}/api/invite/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token,
        userId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to process invite');
    }

    return { success: true };
  } catch (error: unknown) {
    console.error('Error processing invite signup:', error);
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to process invite';
    return { success: false, error: errorMessage };
  }
};

/**
 * Get all pending invites for a company
 * @param companyId Company ID
 */
export const getCompanyInvites = async (
  companyId: string
): Promise<CompanyInvite[]> => {
  const { data, error } = await supabase
    .from('invited_company_users')
    .select('*')
    .eq('company_id', companyId)
    .gt('expires_at', new Date().toISOString());

  if (error) {
    console.error('Error fetching company invites:', error);
    return [];
  }

  return (data as CompanyInvite[]) || [];
};

/**
 * Delete an invite
 * @param inviteId Invite ID to delete
 */
export const deleteInvite = async (inviteId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('invited_company_users')
    .delete()
    .eq('id', inviteId);

  if (error) {
    console.error('Error deleting invite:', error);
    return false;
  }

  return true;
};

/**
 * Resend an invite
 * @param inviteId Invite ID to resend
 * @param expiresInDays Number of days until the new invite expires (default: 7)
 */
export const resendInvite = async (
  inviteId: string,
  expiresInDays: number = 7
): Promise<boolean> => {
  try {
    // Get the invite details
    const { data: invite, error: fetchError } = await supabase
      .from('invited_company_users')
      .select('*, companies(name)')
      .eq('id', inviteId)
      .single();

    if (fetchError || !invite) {
      console.error('Error fetching invite to resend:', fetchError);
      return false;
    }

    // Update the invite with a new expiration date
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expiresInDays);

    const { error } = await supabase
      .from('invited_company_users')
      .update({
        expires_at: expiresAt.toISOString(),
        invited_at: new Date().toISOString()
      })
      .eq('id', inviteId);

    if (error) {
      throw error;
    }

    // Send invite email using our new API
    try {
      // Use window.location to get the base URL if this code runs in the browser
      const baseUrl =
        typeof window !== 'undefined'
          ? `${window.location.protocol}//${window.location.host}`
          : process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000';

      const response = await fetch(`${baseUrl}/api/email/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: invite.email,
          inviteToken: invite.token,
          companyName: invite.companies?.name || 'our company'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error resending invite email:', errorData);
        // Continue the process even if email sending fails
      } else {
        console.log('Invite email resent successfully');
      }
    } catch (emailError) {
      console.error('Error resending invite email:', emailError);
      // Continue the process even if email sending fails
    }

    return true;
  } catch (error: unknown) {
    console.error('Error resending invite:', error);
    return false;
  }
};
